import { A11yModule } from '@angular/cdk/a11y';
import { Component, computed, effect, inject, input, OnDestroy, signal, viewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { DATE_TIME_FORMAT, DateUtilitiesService } from '@utilities/date-utilities.service';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { catchError, finalize, map, Observable, Subscription, tap, throwError } from 'rxjs';
import { fileExtensionValidator, fileSizeValidator } from '../../validators/file-upload.validator';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { ICustomFile } from '../file-uploader/file-uploader.interface';
import { FileUploaderService } from '../file-uploader/file-uploader.service';
import { IconComponent } from '../icon/icon.component';
import {
  ISocialContentFile,
  ISocialContentFileMetadataApi,
  ISocialContentFileViewModel,
} from './social-content-files.interface';
import { SocialContentFilesService } from './social-content-files.service';
import { SocialContentFilesStore } from './social-content-files.store';

@Component({
  imports: [
    A11yModule,
    FileUploaderComponent,
    IconComponent,
    MatButtonModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    TranslateModule,
    TextClampTooltipDirective,
  ],
  providers: [SocialContentFilesStore],
  selector: 'app-social-content-files',
  styleUrl: './social-content-files.component.scss',
  templateUrl: './social-content-files.component.html',
})
export class SocialContentFilesComponent implements OnDestroy {
  private readonly store = inject(SocialContentFilesStore);
  private readonly socialContentFilesService = inject(SocialContentFilesService);
  private readonly imageUtilitiesService = inject(ImageUtilitiesService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly snackBarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly eventTrackingService = inject(EventTrackingService);

  public readonly socialContentId = input.required<string>();
  public readonly campaignId = input.required<string>();
  public readonly briefingId = input<string | null>(null);
  public readonly creatorBriefingMode = input<boolean>(false);

  public readonly fileUploader = viewChild<FileUploaderComponent>('fileUploader');

  public readonly allowedFileExtensions: string[] = [
    'csv',
    'doc',
    'docx',
    'gif',
    'jpeg',
    'jpg',
    'webp',
    'pdf',
    'png',
    'ppt',
    'pptx',
    'txt',
    'xls',
    'xlsx',
  ];
  private readonly maxFileSizeInBytes = 10000000;

  public readonly uploadFileControl = new FormControl<ICustomFile | null>(null, [
    fileExtensionValidator([...this.allowedFileExtensions]),
    fileSizeValidator(this.maxFileSizeInBytes),
  ]);
  public readonly uploading = signal<boolean>(false);
  public readonly attachmentCount = computed(() => this.store.files$().length);
  public readonly previewFile = signal<ISocialContentFileViewModel | null>(null);
  public readonly previewImageLoading = signal<boolean>(false);
  public readonly previewImageFailed = signal<boolean>(false);
  public readonly previewImageUrl = signal<string | null>(null);

  public readonly files = this.store.files$;
  public readonly loading = this.store.loading$;
  public readonly createdByUsers = this.store.createdByUsers$;
  public readonly listSkeletonIndexes = [0, 1] as const;
  public readonly effectiveBriefingId = computed<string | null>(
    () => this.briefingId() ?? this.route.snapshot.params['briefingId'] ?? null,
  );
  public readonly fileRows = computed<ISocialContentFileViewModel[]>(() =>
    this.files()
      .map((file) => this.createViewModel(file))
      .sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp),
  );

  // Populated lazily by syncFileMetadata() below - keyed by the social-content-file's own id
  // (ISocialContentFile.id), which is what the dedicated metadata endpoint takes as a path param
  // (not fileId, the id of the underlying File row). A missing entry means "not fetched yet"; an
  // entry present but null means the fetch failed (createViewModel falls back to the raw fileId).
  private readonly fileMetadata = signal<Map<string, ISocialContentFileMetadataApi | null>>(new Map());
  private readonly fileMetadataSubscriptions = new Map<string, Subscription>();

  private previewObjectUrl?: string;
  private previewSubscription?: Subscription;
  private previewOpener: HTMLElement | null = null;

  public readonly uploadRequest = (file: File): Observable<string> => {
    this.uploading.set(true);

    return this.socialContentFilesService
      .uploadFile({
        briefingId: this.effectiveBriefingId(),
        campaignId: this.campaignId(),
        creatorBriefingMode: this.creatorBriefingMode(),
        file,
        socialContentId: this.socialContentId(),
      })
      .pipe(
        catchError((err: unknown) => {
          this.snackBarService.openError(
            this.translate.instant('components.socialContentFiles.couldNotUploadDocument'),
          );

          return throwError(() => err);
        }),
        tap((socialContentFile) => {
          this.store.addFile(socialContentFile);
          this.eventTrackingService.track(EEventType.campaignAnalyticsAttachmentUploaded, {
            campaignId: socialContentFile.campaignId,
            isBriefing: this.creatorBriefingMode(),
            socialContentId: socialContentFile.socialContentId,
          });
        }),
        map((socialContentFile) => socialContentFile.fileId),
        finalize(() => this.uploading.set(false)),
      );
  };

  constructor() {
    effect(() => {
      if (!this.canLoadFiles()) {
        return;
      }

      this.loadFiles();
    });

    effect(() => {
      if (!this.canLoadFiles()) {
        return;
      }

      this.syncFileMetadata(this.files());
    });
  }

  public ngOnDestroy(): void {
    this.cleanupPreview();
    this.fileMetadataSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.fileMetadataSubscriptions.clear();
  }

  public onUploadClick(): void {
    this.fileUploader()?.onUploadNewClick();
  }

  public removeFile(file: ISocialContentFileViewModel): void {
    if (!file.canDelete) {
      return;
    }

    this.store.deleteFile({
      briefingId: this.effectiveBriefingId(),
      creatorBriefingMode: this.creatorBriefingMode(),
      socialContentFileId: file.id,
    });
  }

  public downloadFile(file: ISocialContentFileViewModel): void {
    // Reuse the already-resolved view-model flag rather than recomputing from file.mimeType,
    // which is always null (see ISocialContentFileApi's comment) - isImage is instead derived from
    // the fetched metadata's mimeType in createViewModel below.
    const fileType = file.isImage ? 'image' : 'file';

    this.imageUtilitiesService.getFile(file.fileId, fileType).subscribe((url) => {
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName;
      link.click();

      setTimeout(() => URL.revokeObjectURL(url), 500);
    });
  }

  public openPreview(file: ISocialContentFileViewModel): void {
    if (!file.isImage) {
      return;
    }

    this.previewOpener = document.activeElement as HTMLElement;
    this.cleanupPreview();
    this.previewFile.set(file);
    this.previewImageLoading.set(true);
    this.previewImageFailed.set(false);

    this.previewSubscription = this.imageUtilitiesService.getFile(file.fileId, 'image').subscribe({
      next: (url) => {
        this.previewObjectUrl = url;
        this.previewImageUrl.set(url);
      },

      error: () => {
        this.previewImageFailed.set(true);
        this.previewImageLoading.set(false);
      },
    });
  }

  public closePreview(event?: Event): void {
    event?.stopPropagation();
    this.cleanupPreview();
    this.previewOpener?.focus();
    this.previewOpener = null;
  }

  private loadFiles(): void {
    this.store.fetchFiles({
      briefingId: this.effectiveBriefingId(),
      campaignId: this.campaignId(),
      creatorBriefingMode: this.creatorBriefingMode(),
      socialContentId: this.socialContentId(),
    });
  }

  private canLoadFiles(): boolean {
    return (
      !!this.campaignId() && !!this.socialContentId() && (!this.creatorBriefingMode() || !!this.effectiveBriefingId())
    );
  }

  private createViewModel(file: ISocialContentFile): ISocialContentFileViewModel {
    // file.originalFilename/mimeType are always null (see ISocialContentFileApi's comment) - the
    // real values come from the async metadata fetch below, keyed by the social-content-file's own
    // id (not fileId). Until that resolves, fall back to a generic file icon/type and surface
    // metadataLoading so the template can show a skeleton in place of the name.
    const metadata = this.fileMetadata().get(file.id);
    const metadataLoading = metadata === undefined;
    const fileType = metadata ? FileUploaderService.getFileTypeByMimeType(metadata.mimeType) : 'file';
    const author = this.getUploadedBy(file);
    const date = file.createdAt
      ? this.dateUtils.formatDate(this.dateUtils.parseDateTimeFromApi(file.createdAt), DATE_TIME_FORMAT)
      : '';

    return {
      ...file,
      canDelete: !this.creatorBriefingMode() || !file.createdBy,
      createdAtTimestamp: this.getCreatedAtTimestamp(file),
      fileIcon: fileType === 'image' ? '/icons/photo.svg' : '/icons/file-description.svg',
      fileName: metadata?.originalFilename ?? file.fileId,
      isImage: fileType === 'image',
      metadataLoading,
      uploadInfo: date ? this.translate.instant('components.socialContentFiles.uploadInfo', { author, date }) : author,
    };
  }

  // Lazily fetches and caches per-row metadata (name/mime-type/size) from the dedicated endpoint
  // (SocialContentFilesService.getFileMetadata) as new rows show up in files() - mirrors the
  // remoteFileMeta pattern in file-uploader.component.ts, but keyed off a Map since this component
  // renders many rows at once rather than a single file. Already-fetched or in-flight ids are
  // skipped so this stays a one-shot fetch per row, not a re-fetch on every files() change.
  private syncFileMetadata(files: ISocialContentFile[]): void {
    const currentIds = new Set(files.map((file) => file.id));
    const knownIds = this.fileMetadata();
    const staleIds = [...knownIds.keys()].filter((id) => !currentIds.has(id));

    if (staleIds.length > 0) {
      this.fileMetadata.update((current) => {
        const next = new Map(current);

        staleIds.forEach((id) => next.delete(id));

        return next;
      });
    }

    for (const id of this.fileMetadataSubscriptions.keys()) {
      if (!currentIds.has(id)) {
        this.fileMetadataSubscriptions.get(id)?.unsubscribe();
        this.fileMetadataSubscriptions.delete(id);
      }
    }

    for (const file of files) {
      if (knownIds.has(file.id) || this.fileMetadataSubscriptions.has(file.id)) {
        continue;
      }

      const subscription = this.socialContentFilesService
        .getFileMetadata({
          briefingId: this.effectiveBriefingId(),
          creatorBriefingMode: this.creatorBriefingMode(),
          socialContentFileId: file.id,
        })
        .subscribe({
          next: (metadata) => {
            this.fileMetadata.update((current) => new Map(current).set(file.id, metadata));
          },

          error: () => {
            this.fileMetadata.update((current) => new Map(current).set(file.id, null));
          },
        });

      subscription.add(() => this.fileMetadataSubscriptions.delete(file.id));
      this.fileMetadataSubscriptions.set(file.id, subscription);
    }
  }

  private getCreatedAtTimestamp(file: ISocialContentFile): number {
    return file.createdAt ? this.dateUtils.parseDateTimeFromApi(file.createdAt).getTime() : 0;
  }

  private getUploadedBy(file: ISocialContentFile): string {
    if (!file.createdBy) {
      return this.translate.instant('components.socialContentFiles.creator');
    }

    if (this.creatorBriefingMode()) {
      return this.translate.instant('components.socialContentFiles.campaignManager');
    }

    return (
      this.createdByUsers().get(file.createdBy)?.name ??
      (AuthService.currentDbUser?.id === file.createdBy ? AuthService.currentDbUser.name : null) ??
      this.translate.instant('components.socialContentFiles.campaignManager')
    );
  }

  private cleanupPreview(): void {
    this.previewSubscription?.unsubscribe();
    this.previewSubscription = undefined;

    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = undefined;
    }

    this.previewFile.set(null);
    this.previewImageUrl.set(null);
    this.previewImageLoading.set(false);
    this.previewImageFailed.set(false);
  }
}
